export type FieldKind = "number" | "select" | "boolean";

export interface TemplateOption {
  label: string;
  value: string;
  multiplier?: number;
  flatFee?: number;
}

export interface TemplateField {
  id: string;
  label: string;
  kind: FieldKind;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  defaultValue: number | string | boolean;
  pricePerUnit?: number;
  flatFee?: number;
  helper?: string;
  options?: TemplateOption[];
}

export interface QuoteTemplate {
  id: string;
  label: string;
  shortLabel: string;
  icon: string;
  summary: string;
  baseFee: number;
  minimum: number;
  confidenceBuffer: number;
  fields: TemplateField[];
  leadQuestions: string[];
}

export interface QuoteResult {
  low: number;
  high: number;
  midpoint: number;
  lineItems: Array<{ label: string; amount: number }>;
}

export type QuoteValues = Record<string, number | string | boolean>;

export const templates: QuoteTemplate[] = [
  {
    id: "cleaning",
    label: "Residential cleaning",
    shortLabel: "Cleaning",
    icon: "sparkles",
    summary: "Bedrooms, bathrooms, extras, and urgency for cleaners who need a fast ballpark quote.",
    baseFee: 72,
    minimum: 95,
    confidenceBuffer: 0.14,
    fields: [
      { id: "bedrooms", label: "Bedrooms", kind: "number", min: 1, max: 7, step: 1, defaultValue: 3, pricePerUnit: 22 },
      { id: "bathrooms", label: "Bathrooms", kind: "number", min: 1, max: 5, step: 1, defaultValue: 2, pricePerUnit: 30 },
      {
        id: "depth",
        label: "Clean type",
        kind: "select",
        defaultValue: "standard",
        options: [
          { label: "Standard", value: "standard", multiplier: 1 },
          { label: "Deep clean", value: "deep", multiplier: 1.45 },
          { label: "End of lease", value: "bond", multiplier: 1.85 }
        ]
      },
      { id: "windows", label: "Inside windows", kind: "boolean", defaultValue: false, flatFee: 45 },
      { id: "urgent", label: "Need it within 48 hours", kind: "boolean", defaultValue: false, flatFee: 35 }
    ],
    leadQuestions: ["preferred date", "parking access", "pets on site"]
  },
  {
    id: "lawn",
    label: "Lawn and garden care",
    shortLabel: "Lawn care",
    icon: "leaf",
    summary: "Yard size, grass height, edging, green waste, and regular maintenance options.",
    baseFee: 55,
    minimum: 75,
    confidenceBuffer: 0.12,
    fields: [
      { id: "area", label: "Lawn area", kind: "number", min: 40, max: 900, step: 20, defaultValue: 180, unit: "m2", pricePerUnit: 0.32 },
      {
        id: "height",
        label: "Grass height",
        kind: "select",
        defaultValue: "normal",
        options: [
          { label: "Normal", value: "normal", multiplier: 1 },
          { label: "Overgrown", value: "overgrown", multiplier: 1.35 },
          { label: "Very overgrown", value: "wild", multiplier: 1.7 }
        ]
      },
      { id: "edging", label: "Include edging", kind: "boolean", defaultValue: true, flatFee: 18 },
      { id: "greenWaste", label: "Green waste removal", kind: "boolean", defaultValue: false, flatFee: 32 }
    ],
    leadQuestions: ["gate width", "green bin available", "repeat schedule"]
  },
  {
    id: "moving",
    label: "Small move estimate",
    shortLabel: "Moving",
    icon: "truck",
    summary: "Rooms, movers, stairs, travel distance, and packing help for small moving operators.",
    baseFee: 140,
    minimum: 180,
    confidenceBuffer: 0.18,
    fields: [
      { id: "rooms", label: "Rooms", kind: "number", min: 1, max: 8, step: 1, defaultValue: 3, pricePerUnit: 58 },
      { id: "distance", label: "Travel distance", kind: "number", min: 1, max: 120, step: 5, defaultValue: 14, unit: "km", pricePerUnit: 3.1 },
      { id: "stairs", label: "Flights of stairs", kind: "number", min: 0, max: 8, step: 1, defaultValue: 1, pricePerUnit: 24 },
      { id: "packing", label: "Packing help", kind: "boolean", defaultValue: false, flatFee: 95 }
    ],
    leadQuestions: ["pickup suburb", "delivery suburb", "lift access"]
  },
  {
    id: "pool",
    label: "Pool service visit",
    shortLabel: "Pool",
    icon: "waves",
    summary: "Pool size, condition, chemical balance, filter cleaning, and one-off recovery visits.",
    baseFee: 68,
    minimum: 88,
    confidenceBuffer: 0.13,
    fields: [
      { id: "volume", label: "Pool volume", kind: "number", min: 15, max: 120, step: 5, defaultValue: 45, unit: "kL", pricePerUnit: 1.05 },
      {
        id: "condition",
        label: "Water condition",
        kind: "select",
        defaultValue: "clear",
        options: [
          { label: "Clear", value: "clear", multiplier: 1 },
          { label: "Cloudy", value: "cloudy", multiplier: 1.35 },
          { label: "Green recovery", value: "green", multiplier: 2.1 }
        ]
      },
      { id: "filter", label: "Filter clean", kind: "boolean", defaultValue: true, flatFee: 25 },
      { id: "chemicals", label: "Include common chemicals", kind: "boolean", defaultValue: true, flatFee: 42 }
    ],
    leadQuestions: ["pool type", "last serviced", "pump access"]
  },
  {
    id: "renovation",
    label: "Handyman renovation",
    shortLabel: "Renovation",
    icon: "hammer",
    summary: "Small jobs, callout, material allowance, urgency, and scope confidence for renovation leads.",
    baseFee: 120,
    minimum: 165,
    confidenceBuffer: 0.22,
    fields: [
      { id: "hours", label: "Estimated labour", kind: "number", min: 1, max: 24, step: 1, defaultValue: 4, unit: "hours", pricePerUnit: 72 },
      { id: "materials", label: "Materials allowance", kind: "number", min: 0, max: 3000, step: 50, defaultValue: 250, unit: "$", pricePerUnit: 1 },
      {
        id: "complexity",
        label: "Complexity",
        kind: "select",
        defaultValue: "normal",
        options: [
          { label: "Simple", value: "simple", multiplier: 0.9 },
          { label: "Normal", value: "normal", multiplier: 1 },
          { label: "Fiddly", value: "fiddly", multiplier: 1.3 }
        ]
      },
      { id: "urgent", label: "Priority booking", kind: "boolean", defaultValue: false, flatFee: 85 }
    ],
    leadQuestions: ["photos attached", "materials supplied", "site access"]
  },
  {
    id: "pet",
    label: "Pet grooming",
    shortLabel: "Pet grooming",
    icon: "paw",
    summary: "Pet size, coat condition, add-ons, and mobile visit pricing for groomers.",
    baseFee: 52,
    minimum: 68,
    confidenceBuffer: 0.12,
    fields: [
      {
        id: "size",
        label: "Pet size",
        kind: "select",
        defaultValue: "medium",
        options: [
          { label: "Small", value: "small", multiplier: 0.85 },
          { label: "Medium", value: "medium", multiplier: 1 },
          { label: "Large", value: "large", multiplier: 1.35 },
          { label: "Extra large", value: "xlarge", multiplier: 1.7 }
        ]
      },
      {
        id: "coat",
        label: "Coat condition",
        kind: "select",
        defaultValue: "normal",
        options: [
          { label: "Normal", value: "normal", multiplier: 1 },
          { label: "Thick", value: "thick", multiplier: 1.25 },
          { label: "Matted", value: "matted", multiplier: 1.65 }
        ]
      },
      { id: "nails", label: "Nail trim", kind: "boolean", defaultValue: true, flatFee: 15 },
      { id: "mobile", label: "Mobile visit", kind: "boolean", defaultValue: false, flatFee: 38 }
    ],
    leadQuestions: ["breed", "temperament", "last groomed"]
  }
];

export function templateById(id: string | null | undefined): QuoteTemplate {
  return templates.find((template) => template.id === id) ?? templates[0];
}

export function defaultValues(template: QuoteTemplate): QuoteValues {
  return Object.fromEntries(template.fields.map((field) => [field.id, field.defaultValue]));
}

export function calculateQuote(template: QuoteTemplate, values: QuoteValues): QuoteResult {
  const lineItems: QuoteResult["lineItems"] = [{ label: "Base visit", amount: template.baseFee }];
  let subtotal = template.baseFee;
  let multiplier = 1;

  for (const field of template.fields) {
    const value = values[field.id] ?? field.defaultValue;

    if (field.kind === "number") {
      const numeric = Number(value || 0);
      const amount = numeric * (field.pricePerUnit ?? 0);
      subtotal += amount;
      if (amount > 0) {
        lineItems.push({ label: field.label, amount });
      }
    }

    if (field.kind === "select") {
      const option = field.options?.find((item) => item.value === value);
      multiplier *= option?.multiplier ?? 1;
      if (option?.flatFee) {
        subtotal += option.flatFee;
        lineItems.push({ label: option.label, amount: option.flatFee });
      }
    }

    if (field.kind === "boolean" && value === true && field.flatFee) {
      subtotal += field.flatFee;
      lineItems.push({ label: field.label, amount: field.flatFee });
    }
  }

  const midpoint = Math.max(template.minimum, subtotal * multiplier);
  const low = roundCurrency(midpoint * (1 - template.confidenceBuffer));
  const high = roundCurrency(midpoint * (1 + template.confidenceBuffer));

  return {
    low,
    high,
    midpoint: roundCurrency(midpoint),
    lineItems: lineItems.map((item) => ({ ...item, amount: roundCurrency(item.amount) }))
  };
}

function roundCurrency(value: number): number {
  return Math.round(value / 5) * 5;
}
